import { useState } from "react"
import { useApolloClient } from "@apollo/client"
import cn from "@/v2-ui/utils/utils.cn"
import { errorToast } from "@/v2-ui/toast"
import Link from "@/v2-backoffice-client/components/link"
import {
  getLayerSheetHref,
  MAP_LAYER_FEATURE_CODE_PREFIX,
} from "@/v2-backoffice-client/layer/layer.constants"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { DotText } from "@/v2-backoffice-client/components/dot"
import { FEATURE_TAB } from "@/v2-backoffice-client/feature/feature.constants"
import { patchTranslationCatalogCache } from "@/v2-backoffice-client/translation/catalog/translation.catalog.cache"
import TranslationCatalogRowDetail from "@/v2-backoffice-client/translation/catalog/TranslationCatalogRowDetail"
import TranslationPendingDiffCell from "@/v2-backoffice-client/translation/pending/TranslationPendingDiffCell"
import { useCatalogChangesQuery } from "@/v2-backoffice-client/translation/publish/translation.changes.query"
import {
  CURRENT_PUBLISH_ENVIRONMENT,
  FACET_LABEL,
  summarisePendingChanges,
} from "@/v2-backoffice-client/translation/publish/translation.publish.utils"
import TranslationPendingRevertButton from "@/v2-backoffice-client/translation/pending/TranslationPendingRevertButton"
import TranslationPendingRevertManyButton from "@/v2-backoffice-client/translation/pending/TranslationPendingRevertManyButton"
import {
  isRevertable,
  type PendingChangeRow,
} from "@/v2-backoffice-client/translation/pending/translation.pending.utils"
import type {
  SupportedLanguage,
  TranslationBoard,
} from "@/v2-backoffice-client/translation/translation.types"

const KIND_LABEL: Record<string, string> = {
  added: "New",
  modified: "Changed",
  removed: "Removed",
}

const KIND_DOT: Record<string, "success-1" | "warning" | "destructive"> = {
  added: "success-1",
  modified: "warning",
  removed: "destructive",
}

// Kinds ranked by how much attention they deserve in the default sort.
const KIND_RANK: Record<string, number> = {
  removed: 2,
  modified: 1,
  added: 0,
}

// Which editor a change belongs to, so expanding a row lands on the facet
// that actually moved instead of always on Text.
const FACET_TAB: Record<
  string,
  (typeof FEATURE_TAB)[keyof typeof FEATURE_TAB]
> = {
  text: FEATURE_TAB.text,
  configuration: FEATURE_TAB.configuration,
  availability: FEATURE_TAB.availability,
  personalData: FEATURE_TAB.availability,
  countries: FEATURE_TAB.availability,
}

export type TranslationPendingViewProps = Omit<
  TableProps<PendingChangeRow>,
  "data" | "columns"
> & {
  board: TranslationBoard
  languages: SupportedLanguage[]
  // Baseline: CreatedAt of the last successful publish run (null = none).
  since: string | null
  sinceLabel: string | null
  // Board refetch, fired after an edit/revert lands.
  onSaved(): void
}

// "Pending publish": the reviewable changelog of everything the next publish
// will ship — every catalog value added / changed / removed since the last
// successful publish to this environment (system-versioning diff), across
// text, configuration, availability, personal data and countries.
//
// The two halves behave differently and the view says so. Text is authored
// here and reaches the apps only when a publish ships it, so it can be
// adjusted or reverted from this list. The other facets were written straight
// to this environment's runtime and mirrored into the catalog, so they are
// already live here and there is nothing to revert — they appear so an
// operator can see what a publish will carry to the OTHER environments.
function TranslationPendingView(props: TranslationPendingViewProps) {
  const { className, board, languages, since, sinceLabel, onSaved, ...rest } =
    props

  const changesQuery = useCatalogChangesQuery({
    variables: { since: since ?? "" },
    skip: !since,
    fetchPolicy: "cache-and-network",
  })
  errorToast(changesQuery.error, {
    title: "Failed to load the pending changelog",
  })

  const client = useApolloClient()
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  const changes = changesQuery.data?.getCatalogChangesSince ?? []
  const summary = summarisePendingChanges(changes)
  const rows: PendingChangeRow[] = changes.map((change) => ({
    key: `${change.Facet}|${change.ObjectType}|${change.ObjectKey}|${change.LanguageCode}`,
    facet: change.Facet,
    objectType: change.ObjectType,
    code: change.ObjectKey,
    languageCode: change.LanguageCode,
    kind: change.ChangeKind,
    oldName: change.OldName ?? "",
    oldDescription: change.OldDescription ?? "",
    newName: change.NewName ?? "",
    newDescription: change.NewDescription ?? "",
    oldValue: change.OldValue ?? "",
    newValue: change.NewValue ?? "",
    revertName: change.OldName ?? null,
    revertDescription: change.OldDescription ?? null,
    user: change.User,
    updatedAt: change.UpdatedAt,
  }))

  const columns = createColumns<PendingChangeRow>({
    enableSelection: true,
    enableSorting: true,
    columns: {
      facet: {
        id: "facet",
        label: "What",
        defaultSize: 120,
        getValue: (row) => FACET_LABEL[row.facet] ?? row.facet,
        enableGrouping: true,
        filter: {
          type: "select",
          placeholder: "Any facet",
          toolbar: true,
          options: Object.values(FACET_LABEL).map((label) => ({
            label,
            value: label,
          })),
        },
      },
      kind: {
        id: "kind",
        label: "Change",
        defaultSize: 110,
        getValue: (row) => KIND_LABEL[row.kind] ?? row.kind,
        sortingFn: (a, b) =>
          (KIND_RANK[a.original.kind] ?? 0) - (KIND_RANK[b.original.kind] ?? 0),
        enableGrouping: true,
        filter: {
          type: "select",
          placeholder: "Any change",
          toolbar: true,
          options: [
            { label: "New", value: "New", dotVariant: "success-1" },
            { label: "Changed", value: "Changed", dotVariant: "warning" },
            { label: "Removed", value: "Removed", dotVariant: "destructive" },
          ],
        },
        renderCell({ row }) {
          return (
            <DotText variant={KIND_DOT[row.original.kind] ?? "muted"}>
              {KIND_LABEL[row.original.kind] ?? row.original.kind}
            </DotText>
          )
        },
      },
      code: {
        id: "code",
        label: "Code",
        defaultSize: 280,
        enableGrouping: true,
      },
      languageCode: {
        id: "languageCode",
        label: "Language",
        defaultSize: 90,
        enableGrouping: true,
        // Only text is per-language; the rest apply to the feature as a whole.
        getValue: (row) => (row.languageCode === "" ? "—" : row.languageCode),
      },
      diff: {
        id: "diff",
        label: "Value (− published / + pending)",
        isWrapped: false,
        getValue: (row) =>
          `${row.oldName} ${row.newName} ${row.oldValue} ${row.newValue}`,
        renderCell({ row }) {
          return <TranslationPendingDiffCell row={row.original} />
        },
      },
      user: {
        id: "user",
        label: "Edited by",
        defaultSize: 140,
        enableGrouping: true,
      },
      updatedAt: {
        id: "updatedAt",
        label: "When",
        defaultSize: 130,
      },
      actions: {
        id: "actions",
        label: "",
        defaultSize: 90,
        getValue: () => "",
        renderCell({ row }) {
          // Non-text facets are already live in this environment: there is no
          // "unpublished" state to roll back to, so undoing one means editing
          // the feature again rather than reverting a changelog line.
          if (!isRevertable(row.original)) return null
          return (
            <TranslationPendingRevertButton
              row={row.original}
              onReverted={onSaved}
            />
          )
        },
      },
    },
  })

  if (!since) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
        <p className="text-sm font-medium">No publish baseline yet</p>
        <p className="max-w-md text-xs text-muted-foreground">
          There is no successful publish run to compare against, so the next
          publish ships the full current state of the catalog. Once a publish
          succeeds, this view lists exactly what changed since.
        </p>
      </div>
    )
  }

  return (
    <Table
      className={cn("translation-pending-table", className)}
      tableId="translation-pending-table"
      data={rows}
      columns={columns}
      // Most consequential first: removals, then edits, then additions;
      // newest on top within each kind.
      initialSorting={[
        { id: "kind", desc: true },
        { id: "updatedAt", desc: true },
      ]}
      // One group per feature code: the per-language texts and the settings
      // of one entry cluster together, so the operator sees everything that
      // moved on a given code in one place.
      initialGrouping={["code"]}
      getRowId={(row) => row.key}
      enableGrouping
      enableInfiniteScroll
      enableRowSelection
      onRowClick={(row) =>
        setExpandedKey((prev) =>
          prev === row.original.key ? null : row.original.key,
        )
      }
      expandedRowId={expandedKey}
      renderRowDetail={(row) => {
        const boardRow = board.rowsByCode.get(row.original.code)
        if (!boardRow) {
          // Layer texts ship through this same publish but are edited on the
          // Layers page (gis codes are excluded from the catalog list) —
          // send the operator to the layer sheet's Details tab.
          if (row.original.code.startsWith(MAP_LAYER_FEATURE_CODE_PREFIX)) {
            return (
              <div className="translation-editor">
                <p className="text-xs text-muted-foreground">
                  This is a map layer&apos;s text. It ships with this publish
                  like everything else, but is edited from the layer itself:{" "}
                  <Link
                    to={getLayerSheetHref(row.original.code)}
                    size="xs"
                    data-feature-code="UI_BackofficeTranslation_Pending_LayerLink"
                  >
                    open {row.original.code} on the Layers page
                  </Link>
                  . You can still Revert it from here.
                </p>
              </div>
            )
          }
          return (
            <div className="translation-editor">
              <p className="text-xs text-muted-foreground">
                This entry has no editable catalog row anymore (its feature was
                removed). Use Revert to restore the previously published state,
                or leave it to ship as a removal.
              </p>
            </div>
          )
        }
        return (
          <TranslationCatalogRowDetail
            key={`${boardRow.code}|${row.original.facet}`}
            row={boardRow}
            languages={languages}
            siblingRows={
              board.categoryByKey.get(boardRow.categoryKey)?.rows ?? []
            }
            defaultTab={FACET_TAB[row.original.facet] ?? FEATURE_TAB.text}
            onSaved={() => {
              onSaved()
              void changesQuery.refetch()
            }}
            // Ordinary saves patch the cached catalog with the exact texts
            // written (no full refetch); only the small changelog query is
            // refetched so the diff lines update.
            onSavedTexts={(texts) => {
              patchTranslationCatalogCache(client.cache, [
                {
                  code: boardRow.code,
                  objectType: boardRow.objectType,
                  texts,
                },
              ])
              void changesQuery.refetch()
            }}
            onDeleted={() => {
              setExpandedKey(null)
              onSaved()
              void changesQuery.refetch()
            }}
            onRenamed={() => {
              setExpandedKey(null)
              onSaved()
              void changesQuery.refetch()
            }}
          />
        )
      }}
      {...rest}
    >
      <Table.Toolbar>
        <Table.Search placeholder="Search code or value..." />
        <span className="text-xs text-muted-foreground">
          {summary.textCount} text and {summary.runtimeCount} setting change(s)
          {sinceLabel
            ? ` since the last publish to ${CURRENT_PUBLISH_ENVIRONMENT} (${sinceLabel} UTC)`
            : ""}
        </span>
        <div className="flex-1" />
        <Table.ToolbarFilters />
        <Table.ToolbarMenu exportFileName="pending-publish" />
      </Table.Toolbar>
      <Table.FilterList />
      <Table.GroupList />

      <Table.Content
        isLoading={changesQuery.loading}
        error={changesQuery.error}
      />

      <Table.SelectionToolbar<PendingChangeRow>>
        {(selected, reset) => (
          <TranslationPendingRevertManyButton
            rows={selected}
            onReverted={() => {
              onSaved()
              reset()
            }}
          />
        )}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default TranslationPendingView
